/*
  Sondermaß- und Brenner-Konfigurator (Claude, 2026-09-19). Gehört zu snippets/bek-sondermass.liquid.
  - Panel "Sondermaß (BxHxT)": Breite/Höhe/Tiefe, Basis-Modell = nächstkleinere Standardbreite.
  - Ebene "Bioethanol Brenner": zeigt immer den Standardbrenner der Basis, "Andere Brennergröße"
    öffnet eine Tabelle. Erlaubte Größen hängen live von Basis (Breite) und Tiefe ab.
  - Zusammenfassung mit Preis, Lieferzeit und Pflicht-Checkbox erscheint, sobald etwas vom Standard abweicht.
  Kauf: Event "bek:prepare-purchase" (AjaxProduct in theme.min.js). Hauptposition = Basis-Variante mit
  sichtbaren Eigenschaften, Kindposition = Aufschlag-Produkt (Menge = Aufschlag / Einzelpreis) je Kamin.
*/
(() => {
  if (customElements.get('bek-sondermass')) return;

  const eur = (cents, currency) => {
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency || 'EUR', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }
  };
  const num = v => String(Math.round(v * 10) / 10).replace('.', ',');
  const cm = v => num(v) + ' cm';
  const parse = raw => {
    const txt = String(raw == null ? '' : raw).trim().replace(/\s|cm/gi, '').replace(',', '.');
    if (!/^\d+(\.\d+)?$/.test(txt)) return NaN;
    return Number(txt);
  };
  const snap = v => Math.round(v * 2) / 2;
  const esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const variantTitle = (model, id) => (model.variants.find(v => String(v.id) === String(id)) || {}).title;
  const burnerKey = txt => {
    const m = String(txt || '').match(/(WELLE\s+X+L)\s*(\d+)/i);
    return m ? m[1].replace(/\s+/g, ' ').toUpperCase() + ' ' + m[2] : null;
  };

  class Sondermass extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      let data;
      try {
        data = JSON.parse(this.querySelector('[data-bek-sm-data]').textContent);
      } catch (e) {
        console.error('[Sondermaß] Konfiguration nicht lesbar', e);
        return;
      }
      this.cfg = data.cfg || {};
      this.bcfg = this.cfg.brenner || {};
      this.catalog = (this.bcfg.reihe || []).map(b => Object.assign({}, b, { key: burnerKey(b.name) })).sort((a, b) => a.l - b.l);
      this.currency = data.currency || 'EUR';
      this.models = (data.models || []).filter(m => m.w > 0).sort((a, b) => a.w - b.w);
      this.current = this.models.find(m => String(m.id) === String(data.current));
      if (!this.current || !this.models.length) return;
      // Nur Modelle derselben Gruppe (z. B. Eckkamin Links/Rechts) als Basis zulassen
      this.models = this.models.filter(m => (m.g || '') === (this.current.g || ''));

      this.section = this.closest('.product-section') || document;
      this.panel = this.querySelector('[data-bek-sm-panel]');
      this.summary = this.querySelector('[data-bek-sm-summary]');
      this.toggle = this.section.querySelector('[data-bek-sm-toggle][aria-controls="' + this.id + '"]');
      this.inputs = Array.from(this.querySelectorAll('[data-bek-sm-dim]'));
      this.consent = this.querySelector('[data-bek-sm-consent]');
      this.errorBox = this.querySelector('[data-bek-sm-error]');
      this.baseBox = this.querySelector('[data-bek-sm-base]');
      this.calcBox = this.querySelector('[data-bek-sm-calc]');
      this.br = {
        wrap: this.querySelector('[data-bek-br]'),
        label: this.querySelector('[data-bek-br-label]'),
        std: this.querySelector('[data-bek-br-std]'),
        other: this.querySelector('[data-bek-br-other]'),
        otherWrap: this.querySelector('[data-bek-br-other-wrap]'),
        spec: this.querySelector('[data-bek-br-spec]'),
        list: this.querySelector('[data-bek-br-list]'),
        note: this.querySelector('[data-bek-br-note]'),
        table: this.querySelector('[data-bek-br-table]'),
        more: this.querySelector('[data-bek-br-more]')
      };
      this.panelOpen = false;
      this.listOpen = false;
      this.showAll = false;
      this.burnerSel = null; // gewählter Brenner-Key, null = Standard

      // Eigene Ebene "Abmessung" (Standard | Sondermaß), z. B. bei vertikalen Familien
      this.stdBtn = this.section.querySelector('[data-bek-sm-std]');
      this.levelInfo = this.section.querySelector('[data-bek-sm-level-info]');
      this.toggle?.addEventListener('click', () => this.openPanel(this.stdBtn ? true : !this.panelOpen));
      this.stdBtn?.addEventListener('click', () => this.openPanel(false));
      this.inputs.forEach(input => {
        input.addEventListener('input', () => this.update(false));
        input.addEventListener('change', () => this.normalize(input));
        input.addEventListener('blur', () => this.normalize(input));
        input.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); this.normalize(input); } });
      });
      this.br.std?.addEventListener('click', () => { this.burnerSel = null; this.listOpen = false; this.update(false); });
      this.br.other?.addEventListener('click', () => { this.listOpen = !this.listOpen; this.update(false); });
      this.br.more?.addEventListener('click', () => { this.showAll = !this.showAll; this.update(false); });
      this.br.table?.addEventListener('click', e => {
        const row = e.target.closest('[data-key]');
        if (!row) return;
        /* a11y: nur bei Tastatur-/AT-Auslösung (detail 0) den Fokus nach dem Neuaufbau zurückholen; Mausklick wie bisher */
        const hadFocus = e.detail === 0 && this.br.table.contains(document.activeElement);
        this.burnerSel = row.dataset.key;
        this.update(false);
        if (hadFocus) this.focusBurnerRow(row.dataset.key);
      });
      /* a11y (Claude, 2026-09-28): Radiogruppe per Pfeiltasten/Home/End bedienbar
         (Roving-Tabindex), Auswahl folgt dem Fokus wie bei nativen Radios. */
      this.br.table?.addEventListener('keydown', e => {
        const rows = Array.from(this.br.table.querySelectorAll('[role="radio"][data-key]'));
        const cur = e.target.closest('[role="radio"][data-key]');
        if (!rows.length || !cur) return;
        let i = rows.indexOf(cur);
        switch (e.key) {
          case 'ArrowDown': case 'ArrowRight': i = (i + 1) % rows.length; break;
          case 'ArrowUp': case 'ArrowLeft': i = (i - 1 + rows.length) % rows.length; break;
          case 'Home': i = 0; break;
          case 'End': i = rows.length - 1; break;
          default: return;
        }
        e.preventDefault();
        const key = rows[i].dataset.key;
        this.burnerSel = key;
        this.update(false);
        this.focusBurnerRow(key);
      });
      this.consent?.addEventListener('change', () => { if (this.consent.checked) this.showError(''); });
      this.section.addEventListener('change', e => { if (!this.contains(e.target)) setTimeout(() => this.update(false), 0); });
      document.addEventListener('bek:prepare-purchase', e => this.preparePurchase(e));
      this.update(false);
      if (/[?&]sondermass=1\b/.test(location.search)) this.openPanel(true);
    }

    get form() {
      return this.section.querySelector('form[action*="/cart/add"]');
    }

    openPanel(show) {
      this.panelOpen = show;
      this.panel.hidden = !show;
      this.toggle?.setAttribute('aria-expanded', String(show));
      if (this.stdBtn) { this.stdBtn.classList.toggle('is-selected', !show); this.stdBtn.setAttribute('aria-pressed', String(!show)); }
      this.update(false);
      if (show) this.inputs[0]?.focus({ preventScroll: true });
    }

    normalize(input) {
      const v = parse(input.value);
      if (Number.isFinite(v)) input.value = String(snap(v)).replace('.', ',');
      input.dataset.shown = '1';
      this.update(false);
    }

    selectedVariantId() {
      const field = this.form && this.form.querySelector('[name="id"]');
      return field ? field.value : null;
    }

    // Aufpreis größerer Brenner in Cent: je 10-cm-Stufe der Preis der Brennerreihe dieser Stufe
    // (Katalogfeld pro10, z. B. XL 100 €, XXL 150 €; sonst brenner.pro10).
    upPrice(std, target) {
      let sum = 0;
      for (let l = std.l + 10; l <= target.l + 1e-9; l += 10) {
        const step = this.catalog.find(b => Math.abs(b.l - l) < 1e-9) || target;
        sum += Number(step.pro10 || this.bcfg.pro10 || 100) * 100;
      }
      return sum;
    }

    /* ---------- Brenner-Regeln ---------- */
    burnerRules(base, depth, width) {
      const ref = this.catalog.find(b => b.key === burnerKey(base.burner)) || null;
      let std = ref;
      if (ref && this.bcfg.modus === 'breite') {
        // Brenner wächst mit der Wunschbreite: Standard = Breite - abzug, auf 10 cm abgerundet, min. kleinster Brenner
        const target = Math.floor((width - Number(this.bcfg.abzug || 30)) / 10 + 1e-9) * 10;
        std = this.catalog.filter(b => b.l <= target + 1e-9).pop() || this.catalog[0];
      }
      if (!std) return { std: null, ref: null, allowed: [], eff: null, forced: false };
      const plus = (this.bcfg.groesser || []).find(g => base.w <= Number(g.bis));
      let plusCm = plus ? Number(plus.cm) : 0;
      // Wunschbreite schmaler als Standardbreite der Basis -> Größer-Regel für schmale Kamine
      if (width < base.w - 1e-9) plusCm = Math.min(plusCm, Number(this.bcfg.groesser_schmaler || 0));
      const maxL = std.l + plusCm;
      const allowed = this.catalog.filter(b => b.l <= maxL && depth + 1e-9 >= Number(b.min_tiefe || 0));
      let eff = allowed.find(b => b.key === std.key) || null;
      let forced = false;
      if (!eff) {
        eff = allowed.filter(b => b.l <= std.l).pop() || null;
        forced = true;
      }
      return { std, ref: ref || std, allowed, eff, forced };
    }

    /* ---------- Berechnung ---------- */
    compute() {
      const cfg = this.cfg;
      const errors = {};
      let w = this.current.w, h = this.current.h, d = this.current.d;
      if (this.panelOpen) {
        const get = dim => parse((this.inputs.find(i => i.dataset.bekSmDim === dim) || {}).value);
        w = get('w'); h = get('h'); d = get('d');
        const check = (dim, v, rule) => {
          if (!Number.isFinite(v)) { errors[dim] = 'Bitte eine Zahl eintragen.'; return; }
          if (Math.abs(v * 2 - Math.round(v * 2)) > 1e-9) { errors[dim] = 'Nur in 0,5-cm-Schritten möglich.'; return; }
          if (v < rule.min || v > rule.max) errors[dim] = 'Möglich sind ' + num(rule.min) + ' bis ' + num(rule.max) + ' cm.';
        };
        check('w', w, cfg.breite); check('h', h, cfg.hoehe); check('d', d, cfg.tiefe);
        if (Object.keys(errors).length) return { errors };
      }

      let base = this.models[0];
      this.models.forEach(m => { if (m.w <= w + 1e-9) base = m; });
      if (cfg.basis === 'breite_hoehe') {
        // Raster Breite x Höhe: größte Breite <= Wunsch, darin größte Höhe <= Wunsch (sonst kleinste)
        const col = this.models.filter(m => Math.abs(m.w - base.w) < 1e-9).sort((a, c) => a.h - c.h);
        base = col[0];
        col.forEach(m => { if (m.h <= h + 1e-9) base = m; });
      }

      const unit = Number(cfg.aufschlag_preis || 100) * 100;
      const per10 = (delta, rate) => delta <= 1e-9 ? 0 : Math.ceil(delta / 10 - 1e-9) * rate * 100;
      const dw = Math.abs(w - base.w);
      let sw = 0;
      if (dw > 1e-9) sw = dw <= Number(cfg.breite.toleranz) + 1e-9 ? Number(cfg.breite.pauschal) * 100 : per10(dw, Number(cfg.breite.pro10));
      const sh = per10(Math.abs(h - base.h), Number(cfg.hoehe.pro10));
      const sd = per10(Math.abs(d - base.d), Number(cfg.tiefe.pro10));

      const rules = this.burnerRules(base, d, w);
      let burner = rules.eff;
      let dropped = false;
      if (this.burnerSel) {
        const pick = rules.allowed.find(b => b.key === this.burnerSel);
        if (pick) burner = pick; else dropped = true;
      }
      let sb = 0;
      if (this.bcfg.modus === 'breite') sb = burner && rules.ref && burner.l > rules.ref.l ? this.upPrice(rules.ref, burner) : 0;
      else if (burner && rules.std && burner.l > rules.std.l) sb = this.upPrice(rules.std, burner);
      else if (burner && rules.std && burner.l < rules.std.l) sb = Number(this.bcfg.kleiner_preis || 0) * 100;
      const burnerChanged = !!(burner && rules.std && burner.key !== rules.std.key);

      const total = sw + sh + sd + sb;
      const title = variantTitle(this.current, this.selectedVariantId());
      const glass = t => /rauch|graphit|grau/i.test(t || '') ? 'r' : (/wei(ss|ß)|klar/i.test(t || '') ? 'w' : '');
      const variant = base.variants.find(v => v.title === title) ||
        (glass(title) && base.variants.find(v => glass(v.title) === glass(title))) ||
        base.variants.find(v => v.available) || base.variants[0];
      const dimsChanged = sw + sh + sd > 0;
      return {
        w, h, d, base, variant, sw, sh, sd, sb, total, rules, burner, burnerChanged, dropped, dimsChanged,
        units: Math.round(total / unit), unitOk: total % unit === 0, custom: dimsChanged || burnerChanged || sb > 0
      };
    }

    update(showAll) {
      const r = this.compute();
      const lim = { w: this.cfg.breite, h: this.cfg.hoehe, d: this.cfg.tiefe };
      this.inputs.forEach(input => {
        const dim = input.dataset.bekSmDim;
        if (showAll) input.dataset.shown = '1';
        const hint = this.querySelector('[data-bek-sm-hint="' + dim + '"]');
        const err = r.errors && r.errors[dim];
        const show = !!(err && input.dataset.shown);
        input.setAttribute('aria-invalid', show ? 'true' : 'false');
        if (hint) {
          hint.classList.toggle('is-error', show);
          hint.textContent = show ? err : num(lim[dim].min) + ' bis ' + num(lim[dim].max) + ' cm';
        }
      });
      this.result = r;
      if (this.levelInfo) this.levelInfo.textContent = this.panelOpen ? '— Sondermaß' + (r.errors ? '' : ' ' + num(r.w) + ' × ' + num(r.h) + ' × ' + cm(r.d)) : this.levelInfo.dataset.std;
      if (r.errors) {
        this.baseBox.innerHTML = '';
        this.summary.hidden = true;
        this.setActive(this.panelOpen);
        return r;
      }
      this.renderBase(r);
      this.renderBurner(r);
      this.renderSummary(r);
      this.setActive(this.panelOpen || r.custom);
      return r;
    }

    renderBase(r) {
      if (!this.panelOpen) { this.baseBox.innerHTML = ''; return; }
      let html = 'Berechnungsbasis: <strong>' + esc(r.base.name) + '</strong>';
      if (r.base.id !== this.current.id) {
        const same = r.base.burner && r.base.burner === this.current.burner;
        const txt = this.bcfg.modus === 'breite' ? 'der Brenner richtet sich nach Ihrer Wunschbreite' : (same ? 'der Standardbrenner bleibt gleich' : 'der Standardbrenner wird ' + (r.base.w > this.current.w ? 'größer' : 'kleiner'));
        const wish = this.cfg.basis === 'breite_hoehe' ? 'Ihre Wunschmaße von ' + num(r.w) + ' × ' + cm(r.h) + ' liegen' : 'Ihre Wunschbreite von ' + cm(r.w) + ' liegt';
        html += '<span class="is-switch">' + wish + ' im Bereich von ' + esc(r.base.name) + '. Wir berechnen auf dieser Basis, ' + txt + '. Die Brennerauswahl darunter passt sich automatisch an.</span>';
      }
      if (!r.custom) html += '<span class="is-switch">Diese Maße entsprechen der Standardgröße. Es fällt kein Aufschlag an.</span>';
      this.baseBox.innerHTML = html;
    }

    renderBurner(r) {
      const b = this.br;
      if (!b.wrap) return;
      const { std, allowed, eff, forced } = r.rules;
      if (!std || !eff) { b.wrap.hidden = true; return; }
      b.wrap.hidden = false;
      const isStd = !this.burnerSel || this.burnerSel === eff.key;
      b.label.textContent = '— ' + r.burner.name + (r.burner.key === std.key ? ' (Standard)' : '');
      b.std.textContent = (forced ? 'Passend: ' : 'Standard: ') + eff.name;
      b.std.classList.toggle('is-selected', isStd && !this.listOpen);
      b.other.classList.toggle('is-selected', !isStd || this.listOpen);
      b.other.setAttribute('aria-expanded', String(this.listOpen));
      b.std.setAttribute('aria-pressed', String(isStd && !this.listOpen));
      b.other.setAttribute('aria-pressed', String(!isStd || this.listOpen));
      const others = allowed.filter(x => x.key !== eff.key);
      b.otherWrap.hidden = !others.length;
      const bits = [r.burner.fuell && 'Füllmenge ' + r.burner.fuell, r.burner.kw, r.burner.dauer && 'Brenndauer ' + r.burner.dauer, r.burner.raum && 'Raumgröße ' + r.burner.raum].filter(Boolean);
      b.spec.textContent = bits.join(' · ');

      const open = (this.listOpen || r.dropped) && others.length > 0;
      b.list.hidden = !open;
      const notes = [];
      if (r.dropped) notes.push('Der gewählte ' + this.burnerSel + ' ist mit diesen Maßen nicht möglich. Wir verwenden den ' + r.burner.name + '.');
      if (forced) notes.push('Bei ' + cm(r.d) + ' Tiefe passt der ' + std.name + ' nicht. Wir setzen den ' + eff.name + ' ein, ohne Aufpreis.');
      else if (this.panelOpen && this.bcfg.modus !== 'breite' && r.base.id !== this.current.id) notes.push('Auswahl passend zu ' + esc(r.base.name) + ' (Ihre Sondermaße).');
      b.note.hidden = !notes.length;
      b.note.textContent = notes.join(' ');
      if (!open) return;

      const hasRaum = this.catalog.some(x => x.raum);
      const cols = [
        ['Füllmenge', x => x.fuell, 'Füllung'],
        ['Heizleistung', x => x.kw, 'Leistung'],
        ['Brenndauer', x => x.dauer, 'Dauer']
      ];
      if (hasRaum) cols.push([this.bcfg.raum_titel || 'Raumgröße', x => x.raum || '–', 'Raum']);
      const rows = allowed.slice().sort((a, c) => c.l - a.l);
      const limit = Number(this.bcfg.sichtbar || 4);
      let visible = rows;
      if (!this.showAll && rows.length > limit) {
        const selIdx = rows.findIndex(x => x.key === r.burner.key);
        const effIdx = rows.findIndex(x => x.key === eff.key);
        const start = Math.max(0, Math.min(effIdx - 2, rows.length - limit));
        visible = rows.slice(start, start + limit);
        if (selIdx >= 0 && !visible.includes(rows[selIdx])) visible = rows;
      }
      const refB = r.rules.ref;
      const sbOf = x => refB && x.l > refB.l ? this.upPrice(refB, x) : 0;
      const price = x => {
        if (x.key === std.key) return 'Standard';
        if (this.bcfg.modus === 'breite') {
          const diff = sbOf(x) - sbOf(std);
          return diff > 0 ? '+ ' + eur(diff, this.currency) : (diff < 0 ? '− ' + eur(-diff, this.currency) : '0 €');
        }
        if (x.l > std.l) return '+ ' + eur(this.upPrice(std, x), this.currency);
        const k = Number(this.bcfg.kleiner_preis || 0) * 100;
        return k ? '+ ' + eur(k, this.currency) : '0 €';
      };
      let html = '';
      const anySel = visible.some(x => x.key === r.burner.key);
      visible.forEach((x, vi) => {
        const sel = x.key === r.burner.key;
        const tab = (sel || (!anySel && vi === 0)) ? '0' : '-1';
        const tag = x.key === std.key ? '<span class="tag">Standard</span>' : (x.l > std.l ? '<span class="tag">größer</span>' : '<span class="tag">kleiner</span>');
        html += '<button type="button" role="radio" aria-checked="' + sel + '" tabindex="' + tab + '" class="bek-br__row' + (sel ? ' is-selected' : '') + '" data-key="' + esc(x.key) + '">' +
          '<span class="r" aria-hidden="true"></span>' +
          '<span class="n"><strong>' + esc(x.name) + '</strong>' + tag + '</span>' +
          '<span class="p">' + price(x) + '</span>' +
          '<span class="m">' + cols.map(c => '<span class="v"><em>' + (c[2] || c[0]) + '</em> ' + esc(c[1](x) || '–') + '</span>').join('') + '</span>' +
          '</button>';
      });
      b.table.innerHTML = html;
      const hiddenCount = rows.length - visible.length;
      b.more.hidden = rows.length <= limit;
      b.more.textContent = this.showAll ? 'Weniger anzeigen' : 'Weitere Brennergrößen anzeigen (' + hiddenCount + ')';
    }

    focusBurnerRow(key) {
      const rows = this.br.table ? this.br.table.querySelectorAll('[role="radio"][data-key]') : [];
      for (let k = 0; k < rows.length; k++) {
        if (rows[k].dataset.key === key) { rows[k].focus(); return; }
      }
    }

    renderSummary(r) {
      this.summary.hidden = !r.custom;
      if (!r.custom) { this.calcBox.innerHTML = ''; return; }
      const row = (label, value) => '<tr><td>' + label + '</td><td>' + value + '</td></tr>';
      const price = r.variant ? r.variant.price : 0;
      const dlt = (v, s) => v === s ? 'Standard ' + cm(s) : cm(v) + ' statt ' + cm(s);
      const plus = v => v ? '+ ' + eur(v, this.currency) : '0 €';
      let html = '<table>' + row('Grundpreis ' + esc(r.base.name) + (r.variant && r.base.variants.length > 1 ? ' (' + esc(r.variant.title) + ')' : ''), eur(price, this.currency));
      if (this.panelOpen) {
        html += row('Breite <small>(' + dlt(r.w, r.base.w) + ')</small>', plus(r.sw)) +
          row('Höhe <small>(' + dlt(r.h, r.base.h) + ')</small>', plus(r.sh)) +
          row('Tiefe <small>(' + dlt(r.d, r.base.d) + ')</small>', plus(r.sd));
      }
      if (r.burner) {
        const std = this.bcfg.modus === 'breite' ? r.rules.ref : r.rules.std;
        html += row('Brenner <small>(' + esc(r.burner.name) + (r.burner.key === std.key ? ', Standard' : ' statt ' + esc(std.name)) + ')</small>', plus(r.sb));
      }
      html += '<tr class="is-total"><td>Gesamtpreis inkl. MwSt.</td><td>' + eur(price + r.total, this.currency) + '</td></tr></table>';
      this.calcBox.innerHTML = html;
    }

    setActive(on) {
      if (this.active === on) return;
      this.active = on;
      this.section.classList?.toggle('bek-sm-active', on);
      this.syncShipping(on);
      this.syncButton(on);
      if (!on) this.showError('');
    }

    showError(msg) {
      if (!this.errorBox) return;
      this.errorBox.hidden = !msg;
      this.errorBox.textContent = msg || '';
    }

    syncShipping(show) {
      const nums = String(this.cfg.versandzeit || '').match(/\d+/g) || [];
      this.section.querySelectorAll('.bek-ship').forEach(ship => {
        const text = ship.querySelectorAll('.bek-ship__v')[0], date = ship.querySelector('[data-bek-ship-date]');
        if (text && text !== date) {
          if (show) { text.dataset.bekSmOrig ??= text.textContent; text.textContent = this.cfg.versandzeit; }
          else if (text.dataset.bekSmOrig != null) text.textContent = text.dataset.bekSmOrig;
        }
        if (date && nums.length >= 2) {
          if (show) {
            date.dataset.bekSmMin ??= date.dataset.min; date.dataset.bekSmMax ??= date.dataset.max;
            date.dataset.min = nums[0]; date.dataset.max = nums[1];
          } else if (date.dataset.bekSmMin != null) {
            date.dataset.min = date.dataset.bekSmMin; date.dataset.max = date.dataset.bekSmMax;
          }
        }
      });
      if (window.bekShipDates) window.bekShipDates();
    }

    syncButton(show) {
      const span = this.form && this.form.querySelector('[data-add-to-cart-text]');
      if (!span) return;
      if (show) { span.dataset.bekSmOrig ??= span.textContent.trim(); span.textContent = this.cfg.button || 'Sonderanfertigung in den Warenkorb'; }
      else if (span.dataset.bekSmOrig) span.textContent = span.dataset.bekSmOrig;
    }

    /* ---------- Kauf ---------- */
    preparePurchase(e) {
      if (!e.detail || e.target !== this.form) return;
      if (!this.panelOpen && !this.burnerSel) return; // reiner Standardkauf
      const purchase = e.detail;
      if (this.panelOpen) this.inputs.forEach(i => this.normalize(i));
      const r = this.update(true);
      if (r.errors) {
        purchase.error = 'Bitte prüfen Sie Ihre Sondermaße: ' + Object.values(r.errors)[0];
        this.panel.scrollIntoView({ behavior: document.documentElement.classList.contains('bek-reduce-motion') ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      if (!r.variant || !r.variant.available) {
        purchase.error = 'Die Basis ' + r.base.name + ' ist in dieser Ausführung derzeit nicht bestellbar. Bitte kontaktieren Sie uns.';
        return;
      }
      const main = purchase.main;
      main.id = String(r.variant.id);
      if (!r.custom) return;
      if (this.consent && !this.consent.checked) {
        purchase.error = 'Bitte bestätigen Sie Ihre Angaben und den Hinweis zur Maßanfertigung.';
        this.showError(purchase.error);
        this.consent.focus();
        return;
      }
      const auf = String(this.cfg.aufschlag_variante || '');
      if (r.total > 0 && (!/^\d+$/.test(auf) || !r.unitOk || r.units < 1)) {
        purchase.error = 'Der Aufschlag konnte nicht berechnet werden. Bitte laden Sie die Seite neu oder kontaktieren Sie uns.';
        this.showError(purchase.error);
        return;
      }
      const cur = this.currency;
      const std = (v, s) => cm(v) + (v === s ? ' (Standard)' : ' (Standard ' + cm(s) + ')');
      const stdB = this.bcfg.modus === 'breite' ? r.rules.ref : r.rules.std;
      const props = {
        'Sonderanfertigung': r.dimsChanged && r.burnerChanged ? 'Sondermaß und anderer Brenner' : (r.dimsChanged ? 'Sondermaß' : 'Anderer Brenner'),
        'Breite': std(r.w, r.base.w),
        'Höhe': std(r.h, r.base.h),
        'Tiefe': std(r.d, r.base.d),
        'Maßangabe': this.cfg.massangabe || 'Außenmaße inkl. 2 cm Blendrahmen',
        'Berechnungsbasis': r.base.name,
        'Brenner': r.burner ? r.burner.name + (r.burner.masse ? ' (' + r.burner.masse + ')' : '') + (stdB && r.burner.key === stdB.key ? ', Standard' : ', statt ' + (stdB ? stdB.name : '-')) : (r.base.burner || '-'),
        'Aufschlag': 'Breite ' + eur(r.sw, cur) + ' + Höhe ' + eur(r.sh, cur) + ' + Tiefe ' + eur(r.sd, cur) + ' + Brenner ' + eur(r.sb, cur) + ' = ' + eur(r.total, cur) + ' je Kamin',
        'Lieferzeit': this.cfg.versandzeit || '',
        'Widerruf': 'Ausschluss bei Maßanfertigung bestätigt',
        '_bek_sm_soll_cent': String(r.total)
      };
      if (r.total > 0) {
        props._bek_sm_units = String(r.units);
        props._bek_sm_variant = auf;
      }
      Object.assign(main.properties, props);
      if (r.total > 0) {
        purchase.items.push({
          id: auf,
          quantity: r.units * main.quantity,
          parent_id: main.id,
          properties: { '_bek_sm_child': '1', 'Für': r.base.name + ', ' + num(r.w) + ' × ' + num(r.h) + ' × ' + cm(r.d) + (r.burner ? ', ' + r.burner.name : '') }
        });
      }
      this.showError('');
    }
  }
  customElements.define('bek-sondermass', Sondermass);
})();
